
import { defineComponent } from 'vue';
import { csvFormatRows, csvParse } from "d3-dsv";

import { distance } from "@wwtelescope/astro";
import { Color, Constellations, Folder, Layer, LayerManager, Poly, RenderContext, Settings, SpreadSheetLayer, WWTControl } from "@wwtelescope/engine";
import { ImageSetType, MarkerScales, PlotTypes, PointScaleTypes, Thumbnail } from "@wwtelescope/engine-types";

import L, { LeafletMouseEvent, Map } from "leaflet";
import { tween } from "femtotween";
import { MiniDSBase, BackgroundImageset, skyBackgroundImagesets} from "@minids/common";

import { ImageSetLayer, Place, Imageset } from "@wwtelescope/engine";

import { ref, onMounted } from 'vue';

import { applyImageSetLayerSetting, applySpreadSheetLayerSetting } from "@wwtelescope/engine-helpers";

//import { proplydsCsv } from "./data/proplyds_data(150)";

//import { jumbosCsv } from "./data/jumbo_data(42)";

import { drawSkyOverlays, initializeConstellationNames, makeAltAzGridText, drawSpreadSheetLayer, layerManagerDraw } from "./wwt-hacks";


function parseCsvTable(csv: string) {
  return csvParse(csv, (d) => {
    return {
      name: d.Name!,  // + makes it a number - we don't want that here
      ra: +d.RA!,
      dec: +d.DEC!,
    };
  });
}

/*const proplydsContainers = document.getElementsByClassName("proplyds-container");

for (let i = 0; i < proplydsContainers.length; i++) {
  proplydsContainers[i].addEventListener("click", function() {
    const proplydsIcon = proplydsContainers[i].querySelector(".proplyds-icon")
    if (proplydsIcon !== null) {
      proplydsIcon.classList.toggle("fa-toggle-on");
      proplydsIcon.classList.toggle("fa-toggle-off");

      if (proplydsIcon.classList.contains("fa-toggle-on")) {
        proplydsIcon.classList.add("active");
      } else {
        proplydsIcon.classList.remove("active");
      }
    }

  });
}

const proplydsTable  = parseCsvTable(proplydsCsv);
type Table = typeof proplydsTable;
type TableRow = typeof proplydsTable[number];

const jumbosTable  = parseCsvTable(jumbosCsv);
type TableJ = typeof jumbosTable;
type TableRowJ = typeof jumbosTable[number];

function formatCsvTable(table: Table): string {
  return csvFormatRows([[
    "Name", "RA", "Dec"
  ]].concat(table.map((d, _i) => {
    return [
      d.name.toString(),
      d.ra.toString(),
      d.dec.toString(),
    ];
  }))).replace(/\n/g, '\r\n');
  // By using a regex, we replace all instances.
  // For WWT implementation reasons (left over from 
  // the Windows client?), we need the line endings 
  // to be CRLF
}

const proplydsString = formatCsvTable(proplydsTable);
const jumbosString = formatCsvTable(jumbosTable);*/

interface MoveOptions {
  instant?: boolean;
  zoomDeg?: number;
  rollRad?: number;
}

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;
const hms2R = (360 * Math.PI) / 4320; // First convert to degrees and then to radians: ((1 / 24) * 360) * (Math.PI / 180);

type LocationRad = {
  longitudeRad: number;
  latitudeRad: number;
};

type EquatorialRad = {
  raRad: number;
  decRad: number;
};

type HorizontalRad = {
  altRad: number;
  azRad: number;
};

interface PointData {
  x: number;
  y: number;
  ra: number;
  dec: number;
  name: string;
  color: Color;
}

type ToolType = "crossfade" | "choose-background" | null;
type SheetType = "text" | "video" | null;

export default defineComponent({

  setup() {
    const fullscreen = ref(false);

    function toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        });
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', () => {
        fullscreen.value = !!document.fullscreenElement;
      });
    });

    return {
      fullscreen,
      toggleFullscreen
    };
  },

  extends: MiniDSBase,
  name: 'MyComponent',
  
  props: {
    wtml: {
      type: Object,
      required: true
    },
    wwtNamespace: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    thumbnailUrl: {
      type: String,
      required: true
    },
    bgWtml: {
      type: String,
      required: true
    },
    bgName: {
      type: String,
      required: true
    },
    fgName: {
      type: String,
      required: true
    },
    title: {
      required: true
    },
    introText: {
      required: true
    },
    introTitle: {
      required: true
    },
    introInstructions: {
      required: true
    },
  },
  
  data() {
    return {
      isMenuOpen: false,
      hiddenTitle: false,
      hiddenIntro: false,
      showIntro: true,
      showBgButton: false,
      showBgSlider: true,
      showFolderView: true,
      //showProplydsButton: true,
      //showJumbosButton: true,
      descriptions: '',
      descrTitles: '',
      descrCredits: '',
      descrCreditsURL: '',
      showDescription: false,
      showLeftMenu: false,
      showTitle: true,
      
 
      showSplashScreen: true,
      imagesetLayers: {} as Record<string, ImageSetLayer>,
      layersLoaded: false,
      positionSet: false,
      imagesetFolder: null as Folder | null,
      backgroundImagesets: [] as BackgroundImageset[],
      layers: {} as Record<string,ImageSetLayer>,

      color0: "#ffffff",
      color1: "#298E00",
      color2: "#ffaa00",
      color3: "#FF00F7",
      colorp: "#8901bd",
      color4: "#00c9ff",
      color5: "#ff0000",
      colorD: "#45aecb",
      // colorRed: "#000000",
      todayColor: "#D6B004",

      currentAllLayer: null as SpreadSheetLayer | null,

      /*proplyds: true,
      proplydsLayer: null as SpreadSheetLayer | null,

      jumbos: true,
      jumbosLayer: null as SpreadSheetLayer | null,*/

      incomingItemSelect: null as Thumbnail | null,
      sheet: null as SheetType,
      showTextTooltip: false,
      showVideoTooltip: false,
      showControls: false,
      currentTool: "crossfade" as ToolType,
      tab: 0,
      map: null as Map | null,
      selectionProximity: 4,
      pointerMoveThreshold: 6,
      isPointerMoving: false,
      pointerStartPosition: null as { x: number; y: number } | null,
      lastClosePt: null as PointData | null
    };
  },


  mounted() {

    this.waitForReady().then(async () => {
      // SpreadSheetLayer.prototype.draw = drawSpreadSheetLayer;


      this.setClockSync(false);
      this.imagesetFolder = await this.loadImageCollection({
        url: this.wtml.layeredimagery,
        loadChildFolders: false
      });
      const children = this.imagesetFolder.get_children() ?? [];
      const layerPromises: Promise<Layer>[] = [];

      children.reverse().forEach((item) => {
        if (!(item instanceof Place)) { return; }
        const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
        if (imageset == null) { return; }
        const name = imageset.get_name();
        layerPromises.push(this.addImageSetLayer({
          url: imageset.get_url(),
          mode: "autodetect",
          name: name,
          goto: false
        }).then((layer) => {
          this.imagesetLayers[name] = layer;
          applyImageSetLayerSetting(layer, ["opacity", 1]); // Here is where you set your initial image's opacity to either 0 or 1 (0 being invisible).
          return layer;
        }));
      });
      
      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.curForegroundImagesetName = this.fgName;
        this.curBackgroundImagesetName = this.bgName;
        this.foregroundOpacity = 75; //set to zero if going to use tween for dramatic fade

        //tween(0, 50, (value) => { this.foregroundOpacity = value; }, {
        //  time: 7500,
          //ease: (t: number) => t * (2 * (1 - t) * 1.75 + t * 0.5),
          //done: () => { this.foregroundOpacity = 50; }
        //});
      });
      
      
      // this.backgroundImagesets = [...skyBackgroundImagesets]; // This is the default background imagery

      /*this.createTableLayer({
        name: "Proplyds",
        referenceFrame: "Sky",
        dataCsv: proplydsString
      }).then((layer) => {
        layer.set_lngColumn(1);
        layer.set_latColumn(2);
        layer.set_markerScale(MarkerScales.world);
        this.applyTableLayerSettings({
          id: layer.id.toString(),
          settings: [
            ["scaleFactor", 0.15],
            ["plotType", PlotTypes.circle],
            ["color", Color.fromHex(this.color3)],
            //["sizeColumn", 4],
            //["pointScaleType", PointScaleTypes.log],
            ["opacity", 100]
          ]
        });
        this.proplydsLayer = layer;
      });

      this.createTableLayer({
        name: "JuMBOS",
        referenceFrame: "Sky",
        dataCsv: jumbosString
      }).then((layer) => {
        layer.set_lngColumn(1);
        layer.set_latColumn(2);
        layer.set_markerScale(MarkerScales.world);
        this.applyTableLayerSettings({
          id: layer.id.toString(),
          settings: [
            ["scaleFactor", 0.15],
            ["plotType", PlotTypes.circle],
            ["color", Color.fromHex(this.color1)],
            // ["sizeColumn", 4],
            //["pointScaleType", PointScaleTypes.log],
            ["opacity", 100]
          ]
        });
        this.jumbosLayer = layer;
      });*/

      this.showControls = !this.mobile;

      /* This section adds extra foreground imagery to the default sky survey menu. 
      This is hard coded and we could find a better way to add them in the future. */
      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("SFD Dust Map (Infrared)", "SFD Dust Map (Infrared)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("2Mass (Infrared)", "2Mass: Imagery (Infrared)") 
        );
      });      

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("WISE (Infrared)", "WISE All Sky (Infrared)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("unWISE (Infrared)", "unWISE color, from W2 and W1 bands") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("PanSTARRS1 3pi (Optical) ", "PanSTARRS1 3pi") 
        );
      });
      
      // this.loadImageCollection({
      //   url: this.bgWtml,
      //   loadChildFolders: true,
      // }).then((folder) => {
      //   this.backgroundImagesets.unshift(
      //     new BackgroundImageset("Digitized Sky Survey (Optical)", "Digitized Sky Survey (Color)") 
      //   );
      // });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("USNOB (Optical)", "USNOB: US Naval Observatory B 1.0 (Synthetic, Optical)") // First name is what you want the menu to look like, second name should be what you see in your WWT collection.
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("Gaia DR2 (Optical)", "Gaia DR2") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("NASA Deep Star Maps (Optical)", "Deep Star Maps 2020") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("GALEX (Ultraviolet)", "GALEX (Ultraviolet)") 
        );
      });

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("ROSAT (X-ray)", "RASS: ROSAT All Sky Survey (X-ray)") 
        );
      });      

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      }).then((_folder) => {
        this.backgroundImagesets.unshift(
          new BackgroundImageset("Fermi (Gamma)", "Fermi LAT 8-year (gamma)") 
        );
      });

      //this.setLayerOpacityForImageSet("Short-wavelength NIRCam Orion Nebula", 1, true)

      Promise.all(layerPromises).then(() => {
        this.layersLoaded = true;
        
        // Set all of the imageset layers to be above the spreadsheet layers
        //this.resetImagesetLayerOrder();

        const splashScreenListener = (_event: KeyboardEvent) => {
          this.showSplashScreen = false;
          window.removeEventListener('keyup', splashScreenListener);
        };
        window.addEventListener('keyup', splashScreenListener);

        window.addEventListener('keyup', (event: KeyboardEvent) => {
          if (["Esc", "Escape"].includes(event.key) && this.showVideoSheet) {
            this.showVideoSheet = false;
          }
        });
      });

      // this.wwtControl._drawSkyOverlays = drawSkyOverlays;
      // LayerManager._draw = layerManagerDraw;

      // wwtZoomDeg is still 0 if we run this here
      // and it was the same in nextTick
      // so give just a bit of a delay
      setTimeout(() => {
        this.positionSet = true;
      }, 100);

    });

  },

  
  computed: {

    wwtControl(): WWTControl {
      return WWTControl.singleton;
    },
    wwtRenderContext(): RenderContext {
      return this.wwtControl.renderContext;
    },
    wwtSettings(): Settings {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return Settings.get_active();
    },
    
    isLoading(): boolean {
      return !this.ready;
    },
    ready(): boolean {
      return this.layersLoaded && this.positionSet;
    },
    smallSize(): boolean {
      return this.$vuetify.display.smAndDown;
    },
    mobile(): boolean {
      return this.smallSize && this.touchscreen;
    },
    cssVars() {
      return {
        '--color-default': this.colorD,
        // '--color-default2': this.colorRed,
				'--color0':this.color0,
				'--color1':this.color1,
				'--color2':this.color2,
				'--color3':this.color3,
				'--color4':this.color4,
				'--color5':this.color5,
        '--app-content-height': this.showTextSheet ? '66%' : '100%',
      };
    },
    popoverCssVars() {
      const x = this.lastClosePt?.x ?? 0;
      const y = this.lastClosePt?.y ?? 0;

      // Note that the canvas takes up the full width
      const leftPct = 100 * x / window.innerWidth;
      const topPct = 100 * (y - 50) / window.innerHeight;
      return {
        '--current-color': this.lastClosePt?.color.toString() ?? "white",
        '--current-left': `${leftPct}%`,
        '--current-top': `${topPct}%`
      };
    },

    
    showTextSheet: {
      get(): boolean {
        return this.sheet === 'text';
      },
      set(_value: boolean) {
        this.selectSheet('text');
        this.showTextTooltip = false;
      }
    },
    showVideoSheet: {
      get(): boolean {
        return this.sheet === "video";
      },
      set(value: boolean) {
        this.selectSheet('video');
        if (!value) {
          const video = document.querySelector("#info-video") as HTMLVideoElement;
          video.pause();
        }
        this.showVideoTooltip = false;
      }
    },
    
    curBackgroundImagesetName: {
      get(): string {
        if (this.wwtBackgroundImageset == null) return "";
        return this.wwtBackgroundImageset.get_name();
      },
      set(name: string) {
        this.setBackgroundImageByName(name);
      }
    },
    
    curForegroundImagesetName: {
      get(): string {
        if (this.wwtForegroundImageset == null) return "";
        return this.wwtForegroundImageset.get_name();
      },
      set(name: string) {
        this.setForegroundImageByName(name);
      }
    },


    foregroundOpacity: {
      get(): number {
        return this.wwtForegroundOpacity;
      },
      set(o: number) {
        this.setForegroundOpacity(o); 
      }
    },
  },

  methods: {

    setSpreadSheetLayerOrder(id: string, order: number) {
      const layer = LayerManager.get_layerList()[id];
      const layers = LayerManager.get_allMaps()[layer.get_referenceFrame()].layers;
      if (order >= 0) {
        const currentIndex = layers.indexOf(layer);
        if (currentIndex > -1) {
          layers.splice(currentIndex, 1);
        }
        LayerManager.get_allMaps()[layer.get_referenceFrame()].layers.splice(order, 0, layer);
      }
    },

    /*closestInView(
      point: { x: number, y: number },
      threshold?: number
    ): PointData | null {

      let minDist = Infinity;
      let closestPt: PointData | null = null;

      const rowSeparator = "\r\n";
      const colSeparator = "\t";

      const raDecDeg = this.findRADecForScreenPoint(point);
      const target = { ra: D2R * raDecDeg.ra, dec: D2R * raDecDeg.dec };

      const layers = [this.proplydsLayer, this.jumbosLayer];
      for (const layer of layers) {
        if (layer == null) {
          continue;
        }

        const inView = layer.getTableDataInView();
        const rows = inView.split(rowSeparator);
        const header = rows.shift();
        if (!header) {
          continue;
        }

        const lngCol = layer.get_lngColumn();
        const latCol = layer.get_latColumn();
        const nameCol = 0;  // All of the data sets have the name in the first column

        for (const row of rows) {
          const values = row.split(colSeparator);
          const ra = D2R * Number(values[lngCol]);
          const dec = D2R * Number(values[latCol]);
          const pt = { ra: ra, dec: dec };
          const dist = distance(target.ra, target.dec, pt.ra, pt.dec);
          if (dist < minDist) {
            closestPt = {
              ...point,
              ra: ra,
              dec: dec,
              name: values[nameCol],
              color: layer.get_color()
            };
            minDist = dist;
          }
        }
      }

      if (closestPt !== null) {
        const closestRADecDeg = { ra: closestPt.ra * R2D, dec: closestPt.dec * R2D };
        const closestScreenPoint = this.findScreenPointForRADec(closestRADecDeg);
        const pixelDist = Math.sqrt((point.x - closestScreenPoint.x) ** 2 + (point.y - closestScreenPoint.y) ** 2);
        if (!threshold || pixelDist < threshold) {
          return closestPt;
        }
      }
      return null;

    },

    wwtOnPointerMove(event: PointerEvent) {
      if (!this.isPointerMoving && this.pointerStartPosition !== null) {
        const dist = Math.sqrt((event.pageX - this.pointerStartPosition.x) ** 2 + (event.pageY - this.pointerStartPosition.y) ** 2);
        if (dist > this.pointerMoveThreshold) {
          this.isPointerMoving = true;
        }
      }
      this.updateLastClosePoint(event);
    },

    wwtOnPointerDown(event: PointerEvent) {
      this.isPointerMoving = false;
      this.pointerStartPosition = { x: event.pageX, y: event.pageY };
    },

    wwtOnPointerUp(event: PointerEvent) {
      if (!this.isPointerMoving) {
        this.updateLastClosePoint(event);
      }
      this.pointerStartPosition = null;
      this.isPointerMoving = false;
    },

    updateLastClosePoint(event: PointerEvent): void {
      const pt = { x: event.offsetX, y: event.offsetY };
      const closestPt = this.closestInView(pt, this.selectionProximity);
      if (closestPt == null && this.lastClosePt == null) {
        return;
      }
      const needsUpdate =
        closestPt == null ||
        this.lastClosePt == null ||
        this.lastClosePt.ra != closestPt.ra ||
        this.lastClosePt.dec != closestPt.dec;
      if (needsUpdate) {
        this.lastClosePt = closestPt;
      }
    },

    wwtSmallestFov(): number {
      // ignore the possibility of rotation
      const w = this.wwtRenderContext.width;
      const h = this.wwtRenderContext.height;
      const fovH = this.wwtRenderContext.get_fovAngle() * D2R;
      const fovW = fovH * w / h;
      return Math.min(fovW, fovH);
    },*/
    
    // circleForLocation(latDeg: number, lonDeg: number): L.Circle<any> {
    //   return L.circle([latDeg, lonDeg], {
    //     color: "#FF0000",
    //     fillColor: "#FF0033",
    //     fillOpacity: 0.5,
    //     radius: 200
    //   });
    // },



    /* Toggles the hamburger menu when the hamburger icon is clicked */
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      this.showTitle = false;
      this.showFolderView = !this.showFolderView;
      //this.showProplydsButton = !this.showProplydsButton;
      //this.showJumbosButton = !this.showJumbosButton;
    },
    
    /* This is for the close button of the intro box */
    closeIntro() {
      this.showIntro = false;
    },
    
    /* This is for the "Toggle description" in the hamburger menu. It opens the intro container */
    openIntro() {
      this.showIntro = !this.showIntro;
      this.showBgButton = false;
      this.showDescription = false;
    },

    closeDescription() {
      this.showDescription = false;
    },

    /* Toggles the background button when the Choose background button is clicked */
    toggleBgButton() {
      this.showBgButton = !this.showBgButton;
      this.hiddenIntro = !this.hiddenIntro;
      this.showIntro = false;
      this.showDescription = false;
      this.showBgSlider = false;
      this.showFolderView = true;
      this.isMenuOpen = false;
      //this.showProplydsButton = true;
      //this.showJumbosButton = true;
    },    

    toggleBgSlider() {
      this.showBgSlider = !this.showBgSlider;
      this.showIntro = false;
      this.showDescription = false;
      this.showBgButton = false;
      this.showFolderView = true;
      this.isMenuOpen = false;
      //this.showProplydsButton = true;
      //this.showJumbosButton = true;
    },

    hideMenu() {
    this.showBgButton = false;
    this.showBgSlider = true;
    },

    toggleFolderView() {
      this.showIntro = false;
      this.showDescription = true;
    },

    hideTitle() {
      this.showTitle = false;
    },

    closeSplashScreen() {
      this.showSplashScreen = false;
    },

    selectSheet(name: SheetType) {
      if (this.sheet == name) {
        this.sheet = null;
      } else {
        this.sheet = name;
      }
    },

    getImageSetLayerIndex(layer: ImageSetLayer): number {
      // find layer in this.wwtActiveLayers
      // this.wwtActiveLayers is a dictionary of {0:id1, 1:id2, 2:id3, ...}
      // get the key item with the value of layer.id
      for (const [key, value] of Object.entries(this.wwtActiveLayers)) {
        if (value === layer.id.toString()) {
          return Number(key);
        }
      }
      return -1;
    },




    setLayerOpacityForImageSet(name: string, opacity: number, setting_opacity_from_ui=false) {
      const layer = this.imagesetLayers[name];
      if (layer != null) {
        // update the image opacity in the WWT control
        applyImageSetLayerSetting(layer, ['opacity', opacity]);

        // update the value for the slider only if we are not setting the opacity from the UI
        if (!setting_opacity_from_ui) {
          const selector = `#items div.item[title='${name}'] input.opacity-range[type='range']`;
          const el = (document.querySelector(selector) as HTMLInputElement);
          if (el != null) {
            el.value = `${opacity * 100}`;
          }
        }

        const toggleSelector = `#items input[type='checkbox'][title='${name}']`;
        const el2 = (document.querySelector(toggleSelector) as HTMLInputElement);
        // truth table: opacity == 0 and el.checked == false => do nothing
        // truth table: opacity == 0 and el.checked == true => set el.checked = false
        // truth table: opacity > 0 and el.checked == false => set el.checked = true
        // truth table: opacity > 0 and el.checked == true => do nothing
        if (el2 != null) {
          if (opacity == 0 && el2.checked) {
            el2.checked = false;
          } else if (opacity > 0 && !el2.checked) {
            el2.checked = true;
          }
        }
        
      }
    },
		
		onItemSelected(place: Place) {
      //this.logTimes(`onItemSelected: ${place.get_name()}`)
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];

      // Give time for the selectedTime changes to propagate
      setTimeout(() => {
        this.$nextTick(() => {
          //if (this.image_out_of_view(place) || this.need_to_zoom_in(place)) {
            this.descriptions = place.htmlDescription;
            this.descrTitles = iset.get_name(); 
            this.descrCredits = iset.get_creditsText();
            this.descrCreditsURL = iset.get_creditsUrl();
            
            this.gotoRADecZoom({
              raRad: hms2R * place.get_RA(), // In the WTML file ra is in hours. Convert to radians (which is what hms2R does)
              decRad: D2R * place.get_dec(), // In the WTML file dec is in degrees. Convert to radians (which is what DR2 does)                
              // raRad: D2R * iset.get_centerX(), // We don't wanna use these lines because the will make the images skip
              // decRad: D2R * iset.get_centerY(),              
              zoomDeg: place.get_zoomLevel(), 
              instant: false
            });
            //this.onOpacityChanged(place, 100, true);
            this.setLayerOpacityForImageSet(iset.get_name(), 1, false);
        });
      }, 10);  
    },
		
		onOpacityChanged(place: Place, opacity: number, move: boolean) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      this.updateImageOpacity(place, opacity);
      

      // this.$nextTick(() => {
      //     this.incomingItemSelect = place;
      //     this.gotoRADecZoom({
      //       raRad: D2R * iset.get_centerX(),
      //       decRad: D2R * iset.get_centerY(),
      //       zoomDeg: this.wwtZoomDeg, //zoom,
      //       instant: false
      //     });
      // });
		},
		
		updateImageOpacity(place: Place, opacity: number) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];
      if (layer == null) { return; }
        // applyImageSetLayerSetting(layer, ["opacity", opacity / 100]);
      // tell setLayerOpacityForImageSet that we are updating from the ui
      this.setLayerOpacityForImageSet(iset.get_name(), opacity / 100, true);
    },
  },

  /*watch: {

    proplyds(on: boolean) {
      if (this.proplydsLayer) {
        applySpreadSheetLayerSetting(this.proplydsLayer, ["opacity", on ? 1.0 : 0.0]);
      }
    },

    jumbos(on: boolean) {
      if(this.jumbosLayer) {
        applySpreadSheetLayerSetting(this.jumbosLayer, ["opacity", on? 1.0 : 0.0]);
      }
    },
    
  }*/
	

	
});

